import { useState } from 'react';
import { HelpCircle, X, MessageSquare, Video, FileText, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quick-answer FAQ — sourced from real first-call questions a new rep would ask.
// Edit this list as support tickets reveal new patterns.
const FAQS = [
  {
    q: 'How does the bot join my meeting?',
    a: 'Paste your Google Meet / Zoom / Teams link into the Meeting Link field and click "Start Call". A bot named "PipeGuide Assistant" will join the meeting within ~30 seconds. Admit it to start live transcription.',
  },
  {
    q: 'Why is the transcript slow or empty?',
    a: 'First, confirm the bot is admitted to the call (not stuck in the lobby). Live transcription starts only after admission. If the bot is in but the transcript stays empty for >30s, end the call and try again with a fresh link.',
  },
  {
    q: 'Can I pin a question to ask later?',
    a: 'Yes — click the 📌 pin icon on any AI-suggested question. It will stay at the top of your suggestions and survive analysis refreshes. Click it again to unpin.',
  },
  {
    q: 'How are tokens used?',
    a: '1 token = 30 minutes of bot recording time. Tokens are consumed only when the bot is actively in a meeting. Top up tokens from Billing when you run low.',
  },
  {
    q: 'How do I invite teammates?',
    a: 'Admin → Team Management → "Invite Member". They get an email link to join your org. Free, no extra cost.',
  },
  {
    q: 'My bot was blocked / "request_blocked" error',
    a: 'This is a transient Recall.ai security check. Wait 30 seconds and try again. If it persists across multiple meetings, contact support.',
  },
];

export function HelpWidget() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('home'); // home | contact
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const sendSupportMessage = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Please add a subject and a message');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/submit`, {
        subject: subject.trim(),
        message: message.trim(),
        category: 'in-app-help-widget',
      });
      toast.success("Got it. I'll reply to you personally within 24 hours.");
      setSubject('');
      setMessage('');
      setView('home');
      setOpen(false);
    } catch (e) {
      toast.error('Could not send right now. Email support@pipeguide.tech directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-brand-primary hover:bg-brand-hover text-white shadow-lg flex items-center justify-center transition-all hover:scale-105"
        title="Help & support"
        data-testid="help-widget-launcher"
      >
        {open ? <X className="w-5 h-5" /> : <HelpCircle className="w-5 h-5" />}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-20 right-5 z-50 w-96 max-w-[calc(100vw-2rem)] bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden flex flex-col"
          style={{ maxHeight: 'calc(100vh - 8rem)' }}
          data-testid="help-widget-panel"
        >
          {/* Header */}
          <div className="bg-brand-primary text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="font-bold text-sm">PipeGuide Help</div>
              <div className="text-[11px] opacity-80">
                {view === 'home' ? 'Quick answers — or message support' : 'Send a message — we read every one'}
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white"
              data-testid="help-widget-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {view === 'home' ? (
            <>
              {/* FAQ list */}
              <div className="overflow-y-auto flex-1 p-3 space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-wide text-zinc-500 px-1 pt-1">
                  Common questions
                </div>
                {FAQS.map((f, i) => (
                  <details
                    key={i}
                    className="bg-zinc-50 border border-zinc-200 rounded-md p-2.5 group"
                    data-testid={`help-faq-${i}`}
                  >
                    <summary className="cursor-pointer text-xs font-semibold text-zinc-900 list-none flex items-start gap-2">
                      <span className="text-brand-primary mt-0.5">›</span>
                      <span className="flex-1">{f.q}</span>
                    </summary>
                    <p className="text-xs text-zinc-600 mt-2 pl-4 leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>

              {/* Footer actions */}
              <div className="border-t border-zinc-200 p-3 space-y-1.5 bg-zinc-50/50">
                <Button
                  variant="outline"
                  className="w-full justify-start text-xs h-8 rounded-md"
                  onClick={() => setView('contact')}
                  data-testid="help-widget-contact-btn"
                >
                  <MessageSquare className="w-3.5 h-3.5 mr-2 text-brand-primary" />
                  Message support
                </Button>
                <a
                  href="https://app.pipeguide.tech/changelog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-xs text-zinc-600 hover:text-brand-primary px-2 py-1.5"
                >
                  <FileText className="w-3.5 h-3.5" />
                  What's new
                  <ExternalLink className="w-3 h-3 ml-auto" />
                </a>
                <a
                  href="https://app.pipeguide.tech/status"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-xs text-zinc-600 hover:text-brand-primary px-2 py-1.5"
                >
                  <Video className="w-3.5 h-3.5" />
                  System status
                  <ExternalLink className="w-3 h-3 ml-auto" />
                </a>
              </div>
            </>
          ) : (
            <>
              {/* Contact form */}
              <div className="p-4 space-y-3 flex-1 overflow-y-auto">
                <div>
                  <label className="text-xs font-medium text-zinc-700">Subject</label>
                  <Input
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder="e.g. Bot won't join my Teams meeting"
                    className="mt-1 rounded-md text-sm"
                    data-testid="help-widget-subject"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-700">Message</label>
                  <Textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Tell us what's happening — include the meeting link or session ID if relevant."
                    className="mt-1 rounded-md text-sm h-28 resize-none"
                    data-testid="help-widget-message"
                  />
                </div>
                <p className="text-[11px] text-zinc-500">
                  We'll reply to the email address on your account. Most replies within 24 hours.
                </p>
              </div>
              <div className="border-t border-zinc-200 p-3 flex gap-2 bg-zinc-50/50">
                <Button
                  variant="ghost"
                  className="flex-1 h-8 text-xs rounded-md"
                  onClick={() => setView('home')}
                  data-testid="help-widget-back"
                >
                  Back
                </Button>
                <Button
                  className="flex-1 h-8 text-xs rounded-md bg-brand-primary hover:bg-brand-hover"
                  onClick={sendSupportMessage}
                  disabled={submitting}
                  data-testid="help-widget-send"
                >
                  {submitting ? 'Sending…' : 'Send message'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
